import authService from "../services/auth.service";
import { readCheckout } from "./billing";
import { signInWithGoogle, signOutFirebase } from "../config/firebase";
import {
  IS_LOGIN,
  AUTH_LOADING,
  AUTH_ERROR,
  SET_USER,
  DESTROY_SESSION,
  VERIFY_TOKEN,
} from "./type";

export const AUTH_TOKEN_KEY = "authToken";
export const USER_ID_KEY = "userId";
export const ASSISTANT_ID_KEY = "activeAssistantId";

// Firebase codes are cryptic; these are the ones worth explaining in the UI.
const FIREBASE_ERRORS = {
  "auth/operation-not-allowed":
    "Google sign-in isn't enabled for this Firebase project. Enable it under Authentication → Sign-in method.",
  "auth/unauthorized-domain":
    "This domain isn't authorized in Firebase. Add it under Authentication → Settings → Authorized domains.",
  "auth/popup-blocked":
    "Your browser blocked the sign-in popup. Allow popups for this site and try again.",
  "auth/invalid-api-key":
    "The Firebase API key is invalid. Check REACT_APP_FIREBASE_API_KEY in .env.",
  "auth/configuration-not-found":
    "Firebase Authentication isn't set up for this project yet. Enable it in the Firebase console.",
};

// The API wraps its payload inconsistently, so check the usual spots.
const readToken = (resp) =>
  resp?.data?.data?.token ||
  resp?.data?.token ||
  resp?.data?.data?.accessToken ||
  resp?.data?.accessToken ||
  null;

const readUser = (resp) => resp?.data?.data?.user || resp?.data?.user || null;

// GET /auth/me answers with the record as the body rather than under `user`.
const readMeBody = (resp) => resp?.data?.data || resp?.data || null;

/**
 * The GET /auth/me body off a verifyToken() response, or null when there is
 * no session behind it. It carries `hasAssistant`, the flag that decides
 * whether a user still needs the onboarding steps or the dashboard.
 *
 * The HTTP code is not enough on its own: a signed-out request can come back
 * 200 carrying { status: false, error: "Not signed in" }, and reading that as
 * a session left the app sitting on a page it had no token for - every
 * request after it answering 401 - instead of asking the user to sign in.
 * A real record always has an id.
 */
export const readMe = (resp) => {
  if (resp?.status !== 200) return null;
  if (resp?.data?.status === false) return null;

  const body = readMeBody(resp);

  return body?.id ? body : null;
};

// Failures come back as { status: false, error }, successes-with-a-note as
// { message }. Without the `error` branch every server-side failure showed the
// generic fallback instead of what actually went wrong.
const readMessage = (resp, fallback) =>
  resp?.data?.data?.error ||
  resp?.data?.error ||
  resp?.data?.data?.message ||
  resp?.data?.message ||
  fallback;

/**
 * Signing in with an unverified address answers 200 — but with no token and
 * `verified: false`, having just mailed a fresh code. That isn't a failed
 * login, it's a detour through the OTP screen, so it's detected before
 * persistSession() can report it as a missing token.
 */
const readPendingVerification = (resp) => {
  const body = resp?.data?.data ?? resp?.data;

  if (!body || typeof body !== "object" || body.verified !== false) return null;

  return {
    email: body.organization?.email || body.email || null,
    message: body.message || "",
  };
};

const persistSession = (dispatch, resp, fallbackUser = null) => {
  const token = readToken(resp);

  if (!token) {
    const message = readMessage(
      resp,
      "Login succeeded but no token was returned.",
    );
    dispatch({ type: AUTH_ERROR, payload: message });
    return false;
  }

  localStorage.setItem(AUTH_TOKEN_KEY, token);
  dispatch({ type: IS_LOGIN, payload: true });
  dispatch({ type: SET_USER, payload: readUser(resp) || fallbackUser });
  dispatch({ type: AUTH_ERROR, payload: "" });
  return true;
};

/**
 * Email + password sign-in.
 * @param {{ email: string, password: string }} payload
 */
export const loginAction = (payload) => async (dispatch) => {
  dispatch({ type: AUTH_LOADING, payload: true });

  try {
    const resp = await authService.login(payload);

    if (resp?.status === 200) {
      const pending = readPendingVerification(resp);

      // Only when there is no session to use. An unconfirmed address that comes
      // back with a token can use the app now and confirm later - and sending
      // that account to the OTP screen throws away a session it was just
      // given, which is what put a paying customer in front of a code box
      // instead of their new assistant.
      if (pending && !readToken(resp)) {
        // Not an error state — the caller sends them to the OTP screen.
        dispatch({ type: AUTH_ERROR, payload: "" });
        return {
          success: false,
          needsVerification: true,
          email: pending.email,
          response: resp,
          message:
            pending.message ||
            "Please verify your email — we just sent you a new code.",
        };
      }

      const success = persistSession(dispatch, resp);
      return {
        success,
        response: resp,
        message: success ? "" : readMessage(resp, "Login failed."),
      };
    }

    const message = readMessage(
      resp,
      resp?.status === 401
        ? "That email and password don't match."
        : "Something went wrong. Please try again.",
    );

    dispatch({ type: AUTH_ERROR, payload: message });
    return { success: false, response: resp, message };
  } finally {
    dispatch({ type: AUTH_LOADING, payload: false });
  }
};

/**
 * Creates the account.
 *
 * Two shapes, decided by whether a plan comes with it:
 *   without plan/interval  the ordinary signup - an OTP is emailed and the
 *                          account is verified before it has a session, so a
 *                          missing token here is not a failure
 *   with plan/interval     the public checkout page - the account is created
 *                          and the body carries a `checkout` for the card
 *                          popup. Deliberately no token: the customer signs
 *                          in after paying
 *
 * @param {{ email: string, password: string, name: string,
 *           plan?: string, interval?: "monthly"|"annual" }} payload
 */
export const signupAction = (payload) => async (dispatch) => {
  dispatch({ type: AUTH_LOADING, payload: true });

  try {
    const resp = await authService.signup(payload);

    if (resp?.status === 200 || resp?.status === 201) {
      if (readToken(resp)) persistSession(dispatch, resp);
      dispatch({ type: AUTH_ERROR, payload: "" });

      const body = resp?.data?.data || resp?.data || {};

      return {
        success: true,
        response: resp,
        // Present only when a plan was bought with the account.
        checkout: readCheckout(body),
        message: body.message || "",
      };
    }

    const message = readMessage(
      resp,
      resp?.status === 409
        ? "An account with that email already exists."
        : "We couldn't create your account. Please try again.",
    );

    dispatch({ type: AUTH_ERROR, payload: message });
    return {
      success: false,
      response: resp,
      // 409 on the checkout page means the email already has a running plan;
      // 403 that the account has more assistants than the plan allows.
      status: resp?.status,
      code: resp?.data?.code || "",
      message,
    };
  } finally {
    dispatch({ type: AUTH_LOADING, payload: false });
  }
};

/**
 * POST /auth/email/verify — confirms the account with the emailed OTP.
 * Some backends hand back a session here; if a token comes with it the user is
 * signed in rather than being sent back to the login screen for nothing.
 * @param {{ email: string, code: string }} payload
 */
export const verifyEmailAction = (payload) => async (dispatch) => {
  dispatch({ type: AUTH_LOADING, payload: true });

  try {
    const resp = await authService.verifyEmail(payload);

    if (resp?.status === 200 || resp?.status === 201) {
      const signedIn =
        Boolean(readToken(resp)) && persistSession(dispatch, resp);
      dispatch({ type: AUTH_ERROR, payload: "" });
      return { success: true, signedIn, response: resp, message: "" };
    }

    const message = readMessage(
      resp,
      resp?.status === 400 || resp?.status === 401 || resp?.status === 422
        ? "That code isn't right or has expired. Try again."
        : "We couldn't verify your email. Please try again.",
    );

    dispatch({ type: AUTH_ERROR, payload: message });
    return { success: false, response: resp, message };
  } finally {
    dispatch({ type: AUTH_LOADING, payload: false });
  }
};

/**
 * POST /auth/email/resend-verification — mails a fresh OTP.
 * @param {{ email: string }} payload
 */
export const resendVerificationAction = (payload) => async (dispatch) => {
  const resp = await authService.resendVerification(payload);

  if (resp?.status === 200 || resp?.status === 201) {
    dispatch({ type: AUTH_ERROR, payload: "" });
    return { success: true, response: resp, message: "" };
  }

  const message = readMessage(
    resp,
    resp?.status === 429
      ? "Too many requests. Wait a moment before asking for another code."
      : "We couldn't send a new code. Please try again.",
  );

  dispatch({ type: AUTH_ERROR, payload: message });
  return { success: false, response: resp, message };
};

/**
 * Google sign-in happens client-side via Firebase; the resulting ID token is
 * exchanged for our own session token at POST /auth/firebase.
 */
export const googleLoginAction = () => async (dispatch) => {
  dispatch({ type: AUTH_LOADING, payload: true });

  try {
    const { idToken, user } = await signInWithGoogle();
    const resp = await authService.firebaseLogin({ idToken });

    if (resp?.status === 200) {
      const ok = persistSession(dispatch, resp, user);
      await dispatch(verifyToken());
      return { success: ok, response: resp };
    }

    const message = readMessage(resp, "We couldn't sign you in with Google.");
    dispatch({ type: AUTH_ERROR, payload: message });
    return { success: false, response: resp, message };
  } catch (error) {
    // The user closing the popup isn't an error worth showing.
    const cancelled =
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/cancelled-popup-request";

    if (!cancelled) {
      // eslint-disable-next-line no-console
      console.error("[auth] Google sign-in failed:", error?.code || "", error);
    }

    const message = cancelled
      ? ""
      : FIREBASE_ERRORS[error?.code] ||
        error?.message ||
        "Google sign-in failed.";

    dispatch({ type: AUTH_ERROR, payload: message });
    return { success: false, cancelled, code: error?.code, message };
  } finally {
    dispatch({ type: AUTH_LOADING, payload: false });
  }
};

export const logoutAction = () => async (dispatch) => {
  try {
    await signOutFirebase();
  } catch {
    // Signing out of Firebase is best-effort; the app session still ends.
  }

  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(ASSISTANT_ID_KEY);
  dispatch({ type: DESTROY_SESSION });
};

export const clearAuthError = () => (dispatch) =>
  dispatch({ type: AUTH_ERROR, payload: "" });

/**
 * Confirms the stored token is still valid (GET /auth/me) and refreshes the
 * cached user. A non-200 ends the session.
 */
export const verifyToken = () => async (dispatch) => {
  const resp = await authService.verifyToken();

  // GET /auth/me returns the user directly as the body:
  // { id, firebaseUid, email, name, avatarUrl, hasAssistant, createdAt,
  //   updatedAt, subscription }
  const user = readMe(resp);

  if (user) {
    localStorage.setItem(USER_ID_KEY, user.id);

    dispatch({ type: VERIFY_TOKEN, payload: user });
    dispatch({ type: IS_LOGIN, payload: true });
    dispatch({ type: SET_USER, payload: user });
  } else {
    // logoutAction() alone just builds a thunk; it has to be dispatched to run.
    dispatch(logoutAction());
  }

  return resp;
};

/**
 * Step 1 of forgot-password: POST /auth/password/forgot — mails a reset code.
 * The API throws when no account exists for the address.
 * @param {{ email: string }} payload
 */
export const forgotPasswordAction = (payload) => async (dispatch) => {
  const resp = await authService.forgotPassword(payload);

  if (resp?.status === 200 || resp?.status === 201) {
    dispatch({ type: AUTH_ERROR, payload: "" });
    return { success: true, response: resp, message: readMessage(resp, "") };
  }

  const message = readMessage(
    resp,
    resp?.status === 404
      ? "We couldn't find an account with that email."
      : "We couldn't send a reset code. Please try again.",
  );

  dispatch({ type: AUTH_ERROR, payload: message });
  return { success: false, response: resp, message };
};

/**
 * Step 2: POST /auth/password/verify-otp — checks the code alone.
 * @param {{ email: string, code: string }} payload
 */
export const verifyResetOtpAction = (payload) => async (dispatch) => {
  const resp = await authService.verifyResetOtp(payload);

  if (resp?.status === 200 || resp?.status === 201) {
    dispatch({ type: AUTH_ERROR, payload: "" });
    return { success: true, response: resp };
  }

  const message = readMessage(
    resp,
    "That code isn't right or has expired. Try again.",
  );

  dispatch({ type: AUTH_ERROR, payload: message });
  return { success: false, response: resp, message };
};

/**
 * Step 3: POST /auth/password/reset — sets the new password. Requires step 2
 * to have succeeded, so no code is sent here.
 * @param {{ email: string, newPassword: string }} payload
 */
export const resetPasswordAction = (payload) => async (dispatch) => {
  const resp = await authService.resetPassword(payload);

  if (resp?.status === 200 || resp?.status === 201) {
    dispatch({ type: AUTH_ERROR, payload: "" });
    return { success: true, response: resp };
  }

  const message = readMessage(
    resp,
    "We couldn't reset your password. Please start again.",
  );

  dispatch({ type: AUTH_ERROR, payload: message });
  return { success: false, response: resp, message };
};
